import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { saveAnalysisMode } from '../slice/chatSlice';
import { analysisModeChanged } from '../slice/api/historySlice';

export const useAnalysisMode = (chatId, savedOn, aiProfile, codeInterpreterEnabled, onSaveError) => {
  const dispatch = useDispatch();
  const [analysisModeOn, setAnalysisModeOn] = useState(false);
  const onRef = useRef(false);
  const chatIdRef = useRef(chatId || '');
  const loadedForRef = useRef(null);
  const onSaveErrorRef = useRef(onSaveError);
  const saveQueueRef = useRef(Promise.resolve());
  const lastSaveRef = useRef({});
  const saveSeqRef = useRef(0);
  const adoptedIdRef = useRef(null);
  chatIdRef.current = chatId || '';
  onSaveErrorRef.current = onSaveError;

  const apply = useCallback((on) => {
    onRef.current = on;
    setAnalysisModeOn(on);
  }, []);

  const save = useCallback((historyId, on) => {
    loadedForRef.current = historyId;
    const seq = ++saveSeqRef.current;
    lastSaveRef.current[historyId] = seq;
    dispatch(analysisModeChanged({ id: historyId, on }));
    saveQueueRef.current = saveQueueRef.current.then(() =>
      saveAnalysisMode(historyId, on).catch(() => {
        if (lastSaveRef.current[historyId] === seq) {
          dispatch(analysisModeChanged({ id: historyId, on: !on }));
          const viewing = chatIdRef.current === historyId || (!chatIdRef.current && adoptedIdRef.current === historyId);
          if (viewing && onRef.current === on) apply(!on);
        }
        onSaveErrorRef.current?.();
      })
    );
    return saveQueueRef.current;
  }, [apply, dispatch]);

  const setAnalysisMode = useCallback((on) => {
    if (onRef.current === on) return;
    apply(on);
    const historyId = chatIdRef.current;
    if (historyId) save(historyId, on);
  }, [apply, save]);

  const adoptNewChat = useCallback((historyId) => {
    if (!historyId || !onRef.current) return Promise.resolve();
    adoptedIdRef.current = historyId;
    return save(historyId, true);
  }, [save]);

  useEffect(() => {
    const id = chatId || '';
    adoptedIdRef.current = null;
    if (loadedForRef.current === id) return;
    if (!id) {
      loadedForRef.current = '';
      apply(false);
      return;
    }
    if (savedOn === undefined) {
      apply(false);
      return;
    }
    loadedForRef.current = id;
    apply(Boolean(savedOn));
  }, [chatId, savedOn, apply]);

  useEffect(() => {
    if (aiProfile && !codeInterpreterEnabled) apply(false);
  }, [aiProfile, codeInterpreterEnabled, apply]);

  return [analysisModeOn, setAnalysisMode, adoptNewChat];
};
